import React from 'react';
import { cn } from '@/lib/utils';
import { ArchiveSubjectIdentity } from './ArchiveSubjectIdentity';
import { ArchiveHeldHereLinks } from './ArchiveHeldHereLinks';
import { getDisplayName } from '../../utils/companionProfiles';
import { formatPersonLifespan } from '../../utils/dateDisplay';

/**
 * Compact person context for non-Overview workspace tabs — keeps the main column wide
 * while preserving whose archive you are in.
 */
export function PersonStudioHeader({
  person,
  storyCount = 0,
  voiceMemoriesCount = 0,
  documentCount = 0,
  onOpenProfile,
  onOpenStories,
  onOpenVoice,
  onOpenDocuments,
  compact = true,
  tabCaption = null,
  className,
}) {
  const name = getDisplayName(person);
  const years = formatPersonLifespan(person);

  return (
    <header
      className={cn(
        'flex flex-col gap-2 rounded-xl border border-[hsl(38_14%_86%)]/55 bg-[hsl(42_28%_99%)]/65 sm:flex-row sm:items-center sm:justify-between',
        compact ? 'px-3 py-2.5 sm:px-4' : 'gap-3 px-4 py-3 sm:px-5',
        className
      )}
      data-testid="person-studio-header"
    >
      <div className="flex min-w-0 flex-1 items-start gap-2.5 sm:items-center sm:gap-3">
        <ArchiveSubjectIdentity
          person={person}
          variant="compact"
          className="shrink-0 pt-0.5 sm:pt-0"
        />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground/55">
            Archive for {name}
          </p>
          <p
            className={cn(
              'font-serif font-medium leading-snug text-[hsl(24_12%_18%)]',
              compact ? 'text-sm sm:text-base' : 'text-base sm:text-lg'
            )}
          >
            {name}
          </p>
          {tabCaption ? (
            <p className="mt-0.5 text-[11px] text-muted-foreground/80">{tabCaption}</p>
          ) : null}
          {years ? (
            <p className="mt-0.5 text-[11px] text-muted-foreground/85 sm:text-xs">{years}</p>
          ) : null}
          <ArchiveHeldHereLinks
            className="mt-1"
            storyCount={storyCount}
            voiceMemoriesCount={voiceMemoriesCount}
            documentCount={documentCount}
            onOpenStories={onOpenStories}
            onOpenVoice={onOpenVoice}
            onOpenDocuments={onOpenDocuments}
          />
        </div>
      </div>
      {typeof onOpenProfile === 'function' ? (
        <button
          type="button"
          className="shrink-0 self-start text-xs font-medium text-[#5c4a32] underline decoration-[#c9a961]/40 underline-offset-4 transition hover:text-[#1c1917] sm:self-center"
          onClick={onOpenProfile}
          data-testid="studio-open-full-profile"
        >
          Overview
        </button>
      ) : null}
    </header>
  );
}

export default PersonStudioHeader;
